import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import usStates from "us-atlas/states-10m.json";
import zipCentroids from "@/data/zipCentroids.json";
import stateCentroids from "@/data/stateCentroids.json";
import { useCustomersMap, type MapCustomer } from "@/lib/hooks";
import { fmtUSDCompact, fmtInt } from "@/lib/format";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";

type Coord = { lng: number; lat: number };

const zips = zipCentroids as Record<string, { lng: number; lat: number; city: string; state: string }>;
const states = stateCentroids as Record<string, Coord>;
// us-atlas ships a TopoJSON object; react-simple-maps accepts it directly.
const geography = usStates as unknown as Record<string, unknown>;

function zip5(pc: string | null): string | null {
  if (!pc) return null;
  const digits = pc.replace(/\D/g, "");
  return digits.length >= 5 && /^\d{5}/.test(pc.trim()) ? digits.slice(0, 5) : null;
}

function resolve(c: MapCustomer): { coord: Coord; city: string | null } | null {
  if ((c.country ?? "US").toUpperCase() !== "US") return null;
  const z = zip5(c.postal_code);
  if (z && zips[z]) return { coord: { lng: zips[z].lng, lat: zips[z].lat }, city: zips[z].city };
  if (c.state && states[c.state]) return { coord: states[c.state], city: null };
  return null;
}

type Point = { c: MapCustomer; coord: Coord; city: string | null };

export default function CustomerMap() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useCustomersMap();
  const [hover, setHover] = useState<Point | null>(null);

  const points = useMemo<Point[]>(() => {
    if (!data) return [];
    return data.customers
      .map((c) => {
        const r = resolve(c);
        return r ? { c, coord: r.coord, city: r.city } : null;
      })
      .filter((p): p is Point => p !== null);
  }, [data]);

  const maxRev = useMemo(() => Math.max(1, ...points.map((p) => p.c.ytd_revenue)), [points]);
  const radius = (rev: number) => 4 + 18 * Math.sqrt(Math.max(0, rev) / maxRev);

  const notShown = data ? data.customers.length - points.length + data.unplaced_count : 0;
  const total = data ? points.length + notShown : 0;

  function exportXls() {
    downloadXlsx(
      stampedName("customer-map"),
      ["Customer", "City", "State", "Postal", "YTD revenue", "YTD quantity"],
      points.map(({ c, city }) => [
        c.customer_name, city, c.state, c.postal_code, c.ytd_revenue, c.ytd_qty,
      ]),
    );
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Customer map</h1>
          <p className="mt-1 text-sm text-ink-500">
            Each dot is a customer, placed at their location and sized by revenue so far this year.
            Hover for details; click a dot to open the customer.
          </p>
        </div>
        <DownloadButton onClick={exportXls} disabled={!points.length} />
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">{(error as Error).message}</div>
      )}

      <section className="card p-4">
        <div className="relative">
          {isLoading ? (
            <div className="h-[520px] animate-pulse rounded bg-ink-100" />
          ) : (
            <>
              <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto" }}>
                <Geographies geography={geography}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#EEF1F4"
                        stroke="#FFFFFF"
                        strokeWidth={0.75}
                        style={{
                          default: { outline: "none" },
                          hover: { fill: "#E2E8F0", outline: "none" },
                          pressed: { outline: "none" },
                        }}
                      />
                    ))
                  }
                </Geographies>
                {points.map(({ c, coord, city }) => (
                  <Marker
                    key={c.customer_id}
                    coordinates={[coord.lng, coord.lat]}
                    onMouseEnter={() => setHover({ c, coord, city })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => navigate(`/customers/${encodeURIComponent(c.customer_id)}`)}
                  >
                    <circle
                      r={radius(c.ytd_revenue)}
                      fill="#2563EB"
                      fillOpacity={0.55}
                      stroke="#1D4ED8"
                      strokeWidth={0.75}
                      style={{ cursor: "pointer" }}
                    />
                  </Marker>
                ))}
              </ComposableMap>

              {hover && (
                <div className="pointer-events-none absolute left-3 top-3 max-w-xs rounded-md border border-ink-200 bg-white/95 px-3 py-2 shadow-sm">
                  <div className="text-sm font-medium text-ink-900">{hover.c.customer_name}</div>
                  <div className="text-xs text-ink-500">
                    {[hover.city, hover.c.state, hover.c.postal_code].filter(Boolean).join(", ") || "—"}
                  </div>
                  <div className="mt-1 text-xs text-ink-700">
                    {fmtUSDCompact(hover.c.ytd_revenue)} · {fmtInt(hover.c.ytd_qty)} units (YTD)
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {data && (
          <p className="mt-3 text-xs text-ink-500">
            Showing {points.length} of {total} customers
            {notShown > 0 ? ` — ${notShown} without a U.S. location aren't plotted.` : "."}
          </p>
        )}
      </section>
    </div>
  );
}
